import { Alert, Dialog, DialogContent } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Form } from "react-final-form";
import { useHistory } from "react-router";
import { Api } from "../../../common/service/Api";
import {
  RegulationSlug,
  RegionGroup,
  WorkflowType,
} from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { usePrimaryOrganization, useSelfOrganizationUser } from "../../hooks/useOrganization";
import { NewRequestForm, newRequestFormValidation } from "../../pages/requests/NewRequestForm";
import { RouteHelpers, Routes } from "../../root/routes";
import { CloseableDialogTitle } from "./CloseableDialogTitle";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import { useRegulationRequestTypes } from "../../hooks/useRegulationRequestTypes";
import {
  createLocationOptions,
  LocationOption,
  shouldIncludeOtherLocationOption,
} from "../../utils/locationUtils";
import { DataSubjectRequestDetailDto } from "../../../common/service/server/dto/DataSubjectRequestDto";
import { DataRequestState, UUIDString } from "../../../common/service/server/Types";
import { PostmarkCreateDsrResponseDto } from "../../../common/service/server/dto/PostmarkInboxDto";
import { useEmploymentCollectionGroups } from "../../hooks/useCollectionGroups";
import { hasCaliforniaEmploymentGroup, hasEeaUkEmploymentGroup } from "../../util/consumerGroups";

export type NewRequestDialogPrefill = {
  subjectFirstName?: string;
  subjectLastName?: string;
  subjectEmailAddress?: string;
  sendEmailVerification?: boolean;
};

type NewRequestDialogProps = {
  open: boolean;
  onClose: () => void;
  prefill?: NewRequestDialogPrefill;
  postmarkConversationId?: UUIDString;
  onPostmarkSuccess?: (response: PostmarkCreateDsrResponseDto) => Promise<void> | void;
  onRequestCreated?: (request: DataSubjectRequestDetailDto) => Promise<void> | void;
};

export const NewRequestDialog = ({
  open,
  onClose,
  prefill,
  postmarkConversationId,
  onPostmarkSuccess,
  onRequestCreated,
}: NewRequestDialogProps) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [org] = usePrimaryOrganization();
  const primaryOrgId = org?.id;
  const [privacyCenter, ,] = usePrivacyCenter(primaryOrgId);
  const [employmentCollectionGroups] = useEmploymentCollectionGroups(primaryOrgId);
  const selfOrgUser = useSelfOrganizationUser();
  const includeOtherLocationOption = shouldIncludeOtherLocationOption({
    hideLocationDropdownOnRequestForm: privacyCenter?.hideLocationDropdownOnRequestForm,
    addOtherOptionToLocationDropdownOnRequestForm:
      privacyCenter?.addOtherOptionToLocationDropdownOnRequestForm,
  });

  const [everRendered, setEverRendered] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    regulationRequestTypes: allRegulationRequestTypes,
    loading: regulationRequestTypesLoading,
    error: regulationRequestTypesError,
  } = useRegulationRequestTypes();

  const navigateAfterCreate = useCallback(
    (requestId: UUIDString, requestState: DataRequestState) => {
      history.push(
        requestState === "PENDING_EMAIL_VERIFICATION" || requestState === "AUTOMATING"
          ? Routes.requests.pending
          : RouteHelpers.requests.detail(requestId),
      );
    },
    [history],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["regulation-request-types"] });
    setErrorMessage(null);
  }, [open, queryClient]);

  // Extract custom fields helper function
  const extractCustomFields = (values: any): Record<string, string> => {
    return Object.keys(values)
      .filter((k) => k.startsWith("custom_"))
      .reduce<Record<string, string>>((acc, k) => {
        const newKey = k.replace(/^custom_/, "");
        const value = values[k];
        // Only include non-empty values
        if (value && value.trim() !== "") {
          acc[newKey] = value;
        }
        return acc;
      }, {});
  };

  // Simplified submission using hosted form pattern
  const handleSubmit = useCallback(
    async (values: any) => {
      const { sendEmailVerification } = values;
      const workflowType: WorkflowType | undefined = values.dataRequestType?.workflowType;
      const requestTypeRequiresVerification = values.dataRequestType?.requireVerification === true;
      const enforceEmailVerification =
        workflowType === WorkflowType.WITHDRAW_CONSENT ||
        workflowType === WorkflowType.PIPEDA_WITHDRAW_CONSENT;
      const effectiveSendEmailVerification = enforceEmailVerification
        ? true
        : Boolean(sendEmailVerification ?? requestTypeRequiresVerification);

      // Determine the correct region code using same pattern as privacy-center form
      let requestCountryRegionCode = null;

      // Follow same pattern as privacy-center form
      if (values.selectedState && values.selectedState.startsWith("US-")) {
        // For US states, use the state code directly (e.g., "US-CA")
        requestCountryRegionCode = values.selectedState;
      } else if (values.selectedState === "OTHER") {
        // Handle "Other" selection specifically
        requestCountryRegionCode = "OTHER";
      } else if (values.region && privacyCenter?.regionRequestTypeMap) {
        // Special handling for "OTHER" region group - use "OTHER" specifically
        if (values.region === "OTHER") {
          requestCountryRegionCode = "OTHER";
        } else {
          // For other region groups (like "EEA_UK"), find the first region code in that group
          const regionCodesInGroup = Object.entries(privacyCenter?.regionRequestTypeMap)
            .filter(([_, mapping]) => mapping.regionGroup === values.region)
            .map(([regionCode, _]) => regionCode);

          if (regionCodesInGroup.length > 0) {
            requestCountryRegionCode = regionCodesInGroup[0]; // This will be "EU" for EEA_UK group
          }
        }
      } else if (values.selectedState) {
        // Fallback for other cases (like individual state regulation codes)
        if (privacyCenter?.regionRequestTypeMap) {
          const regionCodeEntry = Object.entries(privacyCenter?.regionRequestTypeMap).find(
            ([, mapping]) => mapping.regulation === values.selectedState,
          );
          requestCountryRegionCode = regionCodeEntry ? regionCodeEntry[0] : values.selectedState;
        } else {
          requestCountryRegionCode = values.selectedState;
        }
      }

      const submitValues = {
        firstName: values.subjectFirstName,
        lastName: values.subjectLastName,
        email: values.subjectEmailAddress,
        requestType: values.dataRequestType?.workflowType || "KNOW", // Simple string
        requestCountryRegionCode: requestCountryRegionCode,
        phoneNumber: values.subjectPhoneNumber,
        mailingAddress: values.subjectMailingAddress,
        organizationCustomInput: values.organizationCustomInput,
        notes: values.notes,
        context: values.hrRequest ? "EMPLOYEE" : "CONSUMER",
        requestDate: values.requestDate?.format(),
        skipEmailVerification: !effectiveSendEmailVerification,
        subjectCustomFields: extractCustomFields(values),
        appealId: values.appealId,
        descriptionInput: values.description,
        withdrawnConsent: values.withdrawnConsent,
        confirmation: values.confirmation || false,
      };

      try {
        if (postmarkConversationId) {
          const requestTypeId = values.dataRequestType?.id;
          if (!requestTypeId) {
            setErrorMessage("Select a request type");
            return;
          }
          const requestTypeExists = allRegulationRequestTypes.some(
            (type) => type.id === requestTypeId,
          );
          if (!requestTypeExists) {
            setErrorMessage(
              "Request types were updated. Please reselect request type and try again.",
            );
            return;
          }

          const postmarkPayload = {
            regulationRequestTypeId: requestTypeId,
            context: values.hrRequest ? "EMPLOYEE" : "CONSUMER",
            notes: values.notes,
            skipEmailVerification: !effectiveSendEmailVerification,
            subjectFirstName: values.subjectFirstName,
            subjectLastName: values.subjectLastName,
            subjectEmailAddress: values.subjectEmailAddress,
          };

          const response = await Api.postmarkInbox.createDsr(
            primaryOrgId,
            postmarkConversationId,
            postmarkPayload,
          );

          if (onPostmarkSuccess) {
            await onPostmarkSuccess(response);
          }

          onClose();
          navigateAfterCreate(response.dataRequestId, response.dataRequestState);
          return;
        }

        const response = await Api.dataRequest.createRequest(primaryOrgId, submitValues);

        if (values.dataRequestType && (window as any).gtag) {
          (window as any).gtag("event", "request_created", {
            request_type: submitValues.requestType,
            form_type: "internal_dialog",
          });
        }

        if (onRequestCreated) {
          await onRequestCreated(response);
        }

        onClose();
        navigateAfterCreate(response.id, response.state);
      } catch (err: any) {
        setErrorMessage(err?.response?.data?.message ?? "Unknown Error Occurred");
      }
    },
    [
      primaryOrgId,
      onClose,
      navigateAfterCreate,
      allRegulationRequestTypes,
      privacyCenter?.regionRequestTypeMap,
      postmarkConversationId,
      onPostmarkSuccess,
      onRequestCreated,
    ],
  );

  // Enhanced filtering with region group-based approach instead of hardcoded slugs
  const dataRequestTypes = useMemo(() => {
    if (!allRegulationRequestTypes || !org) return [];

    // Build allowed region groups based on organization features
    const allowedRegionGroups: RegionGroup[] = [];

    // Add US state regulations if multistate is enabled
    if (org.featureMultistate) {
      allowedRegionGroups.push(RegionGroup.UNITED_STATES);
    }

    // Add GDPR if enabled
    if (org.featureGdpr) {
      allowedRegionGroups.push(RegionGroup.EEA_UK);
    }

    // Add PIPEDA if enabled
    if (org.featurePipeda) {
      allowedRegionGroups.push(RegionGroup.CANADA);
    }

    // Add OTHER region group if either setting is enabled
    if (includeOtherLocationOption) {
      allowedRegionGroups.push(RegionGroup.OTHER);
    }

    // Simple filtering with CCPA-only special case
    const filtered = allRegulationRequestTypes.filter((requestType) => {
      if (!requestType.regulationLaw) return false;

      const slug = requestType.regulationLaw.slug;
      const regionGroup = requestType.regulationLaw.region?.regionGroup;

      // Special case: CCPA-only orgs (featureMultistate = false) get only CCPA
      if (!org.featureMultistate && slug === RegulationSlug.CCPA) {
        return true;
      }

      // Regular region group filtering for all other cases
      const isIncluded = regionGroup && allowedRegionGroups.includes(regionGroup);

      return isIncluded;
    });

    return filtered;
  }, [allRegulationRequestTypes, org, includeOtherLocationOption]);

  const locationOptions: LocationOption[] = useMemo(() => {
    if (
      !dataRequestTypes ||
      dataRequestTypes.length === 0 ||
      !privacyCenter?.regionRequestTypeMap
    ) {
      return [];
    }

    const { locations } = createLocationOptions(
      dataRequestTypes,
      privacyCenter?.regionRequestTypeMap,
      privacyCenter.hiddenStateSectionAnchors,
    );

    return locations;
  }, [
    dataRequestTypes,
    privacyCenter?.regionRequestTypeMap,
    privacyCenter?.hiddenStateSectionAnchors,
  ]);

  // complete our page load
  useEffect(() => {
    if (!regulationRequestTypesLoading && !regulationRequestTypesError) {
      setEverRendered(true);
    }
  }, [regulationRequestTypesLoading, regulationRequestTypesError]);

  return (
    <>
      <Dialog
        className="get-compliant-dialog"
        fullWidth={true}
        maxWidth={"sm"}
        open={open && everRendered && !!org}
        onClose={onClose}
        aria-labelledby="invite-user-dialog-title"
      >
        <CloseableDialogTitle onClose={onClose}>Create New Request</CloseableDialogTitle>

        <DialogContent>
          {Boolean(errorMessage) && (
            <Alert className="mb-sm" severity="error">
              {errorMessage}
            </Alert>
          )}

          <Form
            keepDirtyOnReinitialize
            onSubmit={handleSubmit}
            validate={(values: any) => newRequestFormValidation(values, privacyCenter)}
            initialValues={{
              requestDate: moment().startOf("day"),
              sendEmailVerification: prefill?.sendEmailVerification ?? true,
              submissionMethod: "Email",
              subjectFirstName: prefill?.subjectFirstName,
              subjectLastName: prefill?.subjectLastName,
              subjectEmailAddress: prefill?.subjectEmailAddress,
            }}
            render={(props) => (
              <NewRequestForm
                onCancel={onClose}
                // See POL-6912 for context to not allow General users to create HR requests
                allowHrRequest={selfOrgUser?.role !== "GENERAL"}
                allowCaliforniaHrRequest={hasCaliforniaEmploymentGroup(employmentCollectionGroups)}
                allowEeaUkHrRequest={hasEeaUkEmploymentGroup(employmentCollectionGroups)}
                isPrivacyEmailInitiated={Boolean(postmarkConversationId)}
                {...props}
                dataRequestTypeOptions={dataRequestTypes}
                locationOptions={locationOptions}
                organization={org}
                privacyCenter={privacyCenter}
              />
            )}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
